import * as React from 'react';
import { injectIntl, InjectedIntlProps, FormattedMessage } from 'react-intl';
import ProfilePicture from '../shared/ProfilePicture';
import {
    getAccountDetails,
    IAccountDetails,
    IAddress,
    ILogin,
    getLoginDetails,
    getAccountGoogleAnalyticsId,
    updateAccountGoogleAnalyticsId,
} from '../../services/account';
import { useEffect, useState } from 'react';
import { PropagateLoader } from 'react-spinners';
import { IAvatar, uploadAvatar } from 'src/services/documents';
import UpdateModal from '../shared/UpdateModal';
import { updateOrganization, updateAccount } from 'src/services/account';
import Modal from 'src/shared/Modal';
import { FileSelector } from 'src/shared/FileSelector';
import { DASHBOARD_PATH, CLIENT_CONTACT_PATH } from '../../constants';
import { Link } from 'react-router-dom';

const DEFAULT_AVATAR_URL = 'https://harmonize.live/static/media/man-303792_640.d574fc46.png';
export default injectIntl((props: InjectedIntlProps & { avatar: IAvatar | null | undefined }) => {
    const [loginDetails, setLoginDetails] = useState<ILogin | null>(null);
    const [accountDetails, setAccountDetails] = useState<IAccountDetails | null>(null);
    const [newAvatar, setNewAvatar] = useState<string | undefined>(undefined);
    const [avatar, setAvatar] = useState<string>('');
    const [uploadingAvatar, setUploadingAvatar] = useState<boolean>(false);
    const [errorUploadingAvatar, setErrorUploadingAvatar] = useState<JSX.Element | null>(null);
    const [googleTrackingCode, setGoogleTrackingCode] = useState<string | undefined>(undefined);

    const pullAccountDetails = async () => {
        const login = await getLoginDetails();
        if (login.loginDetails) {
            setLoginDetails(login.loginDetails);
        }
        const ret = await getAccountDetails();
        if (ret.accountDetails) {
            setAccountDetails(ret.accountDetails);
        }
        const { id } = await getAccountGoogleAnalyticsId();
        if (id) {
            setGoogleTrackingCode(id);
        }
    };

    useEffect(() => {
        pullAccountDetails();
    }, []);

    useEffect(() => {
        if (!avatar) setAvatar(props.avatar ? props.avatar.url : '');
    }, [props.avatar]);

    if (accountDetails === null) {
        return (
            <div className="tab-pane show active wrap">
                <div className="container fluid">
                    <div className="row">
                        <PropagateLoader />
                    </div>
                </div>
            </div>
        );
    }

    const orgNameStyle: React.CSSProperties = {
        paddingTop: '10px',
        paddingLeft: '20px',
    };

    const sectionStyle = {
        padding: 40,
    };

    const rightPanel = {
        borderLeft: '1px solid #EEE',
        paddingLeft: 20,
    };

    const sectionHeaderStyle = {
        paddingBottom: 10,
    };

    const expandAddress = (address: IAddress) => {
        const fields = [
            address.firstLine,
            address.secondLine,
            `${address.city}, ${address.state} ${address.zipCode}`,
        ];
        return (
            <dd key="addr0">
                {fields.map((f, i) =>
                    f != null && f.length > 0 ? (
                        <div key={`${f}-${i}`}>
                            {f}
                            <br />
                        </div>
                    ) : null
                )}
            </dd>
        );
    };

    const handleSetAvatar = async (avatarUrl: string) => {
        if (!avatarUrl) return;
        const success = await updateOrganization({
            ...accountDetails.organization,
            avatar: avatarUrl,
        });
        if (success) {
            // TODO is this mixing jquery, yeah but we need to for bootstrap JS based functionality
            $('#logo-modal').modal('hide');
            setAvatar(avatarUrl);
            setNewAvatar(undefined);
            pullAccountDetails();
        } else {
            alert('Could not set the organization logo. Please try again');
        }
    };

    const logoActionButtons = [
        <button
            type="button"
            className="btn"
            key="cancel-button"
            data-dismiss="modal"
            id="close-button">
            Cancel
        </button>,
        newAvatar ? (
            <button
                type="button"
                disabled={newAvatar === undefined}
                className="btn btn-primary"
                key="set-logo-button"
                onClick={() => handleSetAvatar(newAvatar)}>
                Set Logo
            </button>
        ) : null,
        !newAvatar && avatar && avatar !== DEFAULT_AVATAR_URL ? (
            <button
                type="button"
                disabled={newAvatar === undefined}
                className="btn btn-primary"
                key="reset-logo-button"
                onClick={() => handleSetAvatar(DEFAULT_AVATAR_URL)}>
                Set Default
            </button>
        ) : null,
    ];

    return (
        <div className="tab-pane show active wrap">
            <div className="container fluid">
                <div className="row">
                    <div className="col-lg-12 breadcrumb-col">
                        <ul>
                            <li key="bc1">
                                <a href={DASHBOARD_PATH}>
                                    <FormattedMessage
                                        id="account.breadcrumb.dashboard"
                                        defaultMessage="Dashboard"
                                    />
                                </a>
                            </li>
                            <li key="bc2">
                                <i className="fas fa-angle-right" />
                            </li>
                            <li key="bc3">
                                <FormattedMessage
                                    id="account.breadcrumb.title"
                                    defaultMessage="Account Profile"
                                />
                            </li>
                        </ul>
                    </div>
                </div>

                <br />

                <div className="row">
                    <div className="col-lg-12">
                        <h2>
                            <FormattedMessage id="account.title" defaultMessage="Account Profile" />
                        </h2>
                    </div>
                </div>

                <br />

                <div className="row">
                    <div className="col-lg-12">
                        <div className="container">
                            <div className="row">
                                <div className="col-xs-2">
                                    <ProfilePicture size={60} avatar={avatar} />
                                </div>
                                <div className="col-xs-10" style={orgNameStyle}>
                                    <h4>{accountDetails.organization.orgName}</h4>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <br />
                <br />

                <div className="row">
                    <div className="col">
                        <FormattedMessage
                            tagName="h4"
                            id="dash.account.info.hdr"
                            defaultMessage="Account Owner"
                        />
                    </div>
                </div>

                <div className="row">
                    <div className="col" style={rightPanel}>
                        <section className="container" style={{ paddingTop: 40 }}>
                            <UpdateModal
                                fields={[
                                    {
                                        name: 'name',
                                        label: 'Full Name',
                                        initialValue: accountDetails.owner.fullName,
                                    },
                                    {
                                        name: 'email',
                                        label: 'Email Address',
                                        initialValue: accountDetails.owner.email,
                                    },
                                    {
                                        name: 'address1',
                                        label: 'Address Line 1',
                                        initialValue: accountDetails.owner.address.firstLine,
                                    },
                                    {
                                        name: 'address2',
                                        label: 'Address Line 2',
                                        initialValue: accountDetails.owner.address.secondLine,
                                        required: false,
                                    },
                                    {
                                        name: 'city',
                                        label: 'City',
                                        initialValue: accountDetails.owner.address.city,
                                    },
                                    {
                                        name: 'state',
                                        label: 'State',
                                        initialValue: accountDetails.owner.address.state,
                                    },
                                    {
                                        name: 'zip',
                                        label: 'Zip',
                                        initialValue: accountDetails.owner.address.zipCode,
                                        isValid: (value: string) => value.length === 5,
                                        errorText: 'Zip code must be 5 characters',
                                    },
                                    {
                                        name: 'workPhone',
                                        label: 'Work Phone',
                                        initialValue: accountDetails.owner.phone.work,
                                    },
                                    {
                                        name: 'mobilePhone',
                                        label: 'Mobile Phone',
                                        initialValue: accountDetails.owner.phone.mobile,
                                    },
                                    {
                                        name: '_organization',
                                        label: '_organization',
                                        hidden: true,
                                        initialValue: accountDetails.owner.organization,
                                    },
                                ]}
                                modalId="account-update"
                                onSubmit={async (values) => {
                                    const successful = await updateAccount({
                                        email: values.email,
                                        fullName: values.name,
                                        organization: values._organization,
                                        phone: {
                                            mobile: values.mobilePhone,
                                            work: values.workPhone,
                                        },
                                        address: {
                                            city: values.city,
                                            firstLine: values.address1,
                                            secondLine: values.address2,
                                            state: values.state,
                                            zipCode: values.zip,
                                        },
                                    });
                                    if (successful) {
                                        $('#account-update').modal('hide');
                                        pullAccountDetails();
                                    } else {
                                        alert(
                                            'There was an error updating the account. Please try again.'
                                        );
                                    }
                                }}
                            />
                            <header>
                                <h5 style={{ display: 'inline', marginRight: 10 }}>Personal Info</h5>

                                <div className="t-edit" style={{ display: 'inline' }}>
                                    <a
                                        href="#"
                                        data-toggle="modal"
                                        data-target="#account-update"
                                        title="Edit">
                                        <i className="fas fa-edit" />
                                    </a>
                                </div>
                            </header>

                            <dl>
                                <dt key="1">Name</dt>
                                <dd key="2">{accountDetails.owner.fullName}</dd>
                                <dt key="3">Address</dt>
                                {expandAddress(accountDetails.owner.address)}
                            </dl>
                        </section>
                    </div>

                    <div className="col">
                        <section className="container" style={sectionStyle}>
                            <dl>
                                <dt key="1">Work Phone</dt>
                                <dd key="2">{accountDetails.owner.phone.work || '–'}</dd>
                                <dt key="3">Mobile Phone</dt>
                                <dd key="4">{accountDetails.owner.phone.mobile || '–'}</dd>
                                <dt key="5">Email</dt>
                                <dd key="6">{accountDetails.owner.email}</dd>
                            </dl>
                        </section>
                    </div>
                </div>

                <br />
                <br />

                <div className="row">
                    <div className="col-xs-12">
                        <FormattedMessage
                            tagName="h4"
                            id="dash.profile.org.hdr"
                            defaultMessage="Organization Information"
                        />
                    </div>
                </div>

                <div className="row">
                    <div className="col">
                        <section className="container" style={sectionStyle}>
                            <Modal
                                actionButtons={logoActionButtons}
                                id="logo-modal"
                                title="Upload a New Logo">
                                <FormattedMessage
                                    tagName="p"
                                    id="profile.avatar.txt"
                                    defaultMessage={`
                                    Logos can be any image file type. Maximum file size is 3MB. 
                                    JPEG images at least 400 pixels wide and square work best. 
                                    Images will be automatically scaled and centered for display.
                                    `}
                                />
                                {uploadingAvatar ? (
                                    <noscript />
                                ) : (
                                    <FileSelector
                                        clearOnSelect={true}
                                        handleFileChange={async (file) => {
                                            setUploadingAvatar(true);
                                            try {
                                                if (file === null) return;
                                                const avatarInfo = await uploadAvatar(file);
                                                if (!avatarInfo || !avatarInfo.url) {
                                                    setErrorUploadingAvatar(
                                                        <FormattedMessage
                                                            tagName="p"
                                                            id="profile.avatar.error"
                                                            defaultMessage={`Error uploading avatar. Please try again. `}
                                                        />
                                                    );
                                                } else {
                                                    setNewAvatar(avatarInfo.url);
                                                }
                                            } catch (ex) {
                                                setErrorUploadingAvatar(
                                                    <FormattedMessage
                                                        tagName="p"
                                                        id="profile.avatar.error"
                                                        defaultMessage={`Error uploading avatar. Please try again. `}
                                                    />
                                                );
                                            }
                                            setUploadingAvatar(false);
                                        }}
                                    />
                                )}
                                {errorUploadingAvatar || <noscript />}
                                {uploadingAvatar ? (
                                    <div className="p-5 d-flex justify-content-center">
                                        <PropagateLoader
                                            size={15}
                                            loading={uploadingAvatar}
                                            color="#29447b"
                                        />
                                    </div>
                                ) : (
                                    <noscript />
                                )}

                                <img src={newAvatar} style={{ width: '100%', marginTop: 15 }} />
                            </Modal>
                            <header>
                                <h5
                                    style={{
                                        ...sectionHeaderStyle,
                                        display: 'inline',
                                        marginRight: 10,
                                    }}>
                                    Logo
                                </h5>

                                <div className="t-edit" style={{ display: 'inline' }}>
                                    <a
                                        href="#"
                                        onClick={() => setNewAvatar('')}
                                        data-toggle="modal"
                                        data-target="#logo-modal"
                                        title="Edit">
                                        <i className="fas fa-edit" />
                                    </a>
                                </div>
                            </header>

                            <div style={{ marginTop: 20 }}>
                                <ProfilePicture size={200} avatar={avatar} />
                            </div>
                        </section>
                    </div>

                    <div className="col" style={rightPanel}>
                        <section className="container" style={sectionStyle}>
                            <UpdateModal
                                fields={[
                                    {
                                        label: 'Organization Name',
                                        name: 'orgName',
                                        initialValue: accountDetails.organization.orgName,
                                        hidden: false,
                                        editable: false,
                                        helpText: (
                                            <span>
                                                Please{' '}
                                                <Link
                                                    onClick={() =>
                                                        $('#organization-update').modal('hide')
                                                    }
                                                    to={CLIENT_CONTACT_PATH}>
                                                    contact us
                                                </Link>{' '}
                                                directly to change the name of your organization.
                                            </span>
                                        ),
                                    },
                                    {
                                        label: 'Main Email',
                                        name: 'email',
                                        hidden: false,
                                        editable: false,
                                        initialValue: accountDetails.organization.email,
                                        helpText: (
                                            <span>
                                                Please{' '}
                                                <Link
                                                    onClick={() =>
                                                        $('#organization-update').modal('hide')
                                                    }
                                                    to={CLIENT_CONTACT_PATH}>
                                                    contact us
                                                </Link>{' '}
                                                directly to change the email of your organization.
                                            </span>
                                        ),
                                    },
                                    {
                                        label: 'Address Line 1',
                                        name: 'address1',
                                        initialValue: accountDetails.organization.address.firstLine,
                                    },
                                    {
                                        label: 'Address Line 2',
                                        name: 'address2',
                                        initialValue:
                                            accountDetails.organization.address.secondLine,
                                        required: false,
                                    },
                                    {
                                        label: 'City',
                                        name: 'city',
                                        initialValue: accountDetails.organization.address.city,
                                    },
                                    {
                                        label: 'State',
                                        name: 'state',
                                        initialValue: accountDetails.organization.address.state,
                                    },
                                    {
                                        label: 'Zip',
                                        name: 'zip',
                                        initialValue: accountDetails.organization.address.zipCode,
                                        isValid: (value: string) => value.length === 5,
                                        errorText: 'Zip code must be 5 characters',
                                    },
                                    {
                                        label: 'Main Phone',
                                        name: 'phone',
                                        initialValue: accountDetails.organization.phone,
                                    },
                                    {
                                        label: 'Website',
                                        name: 'website',
                                        initialValue: accountDetails.organization.website,
                                    },
                                    {
                                        label: 'Google Analytics Tracking ID',
                                        name: 'googleTrackingCode',
                                        initialValue: googleTrackingCode,
                                    },
                                    {
                                        label: '_avatar',
                                        name: '_avatar',
                                        initialValue: accountDetails.organization.avatar,
                                        hidden: true,
                                    },
                                ]}
                                modalId="organization-update"
                                onSubmit={async (values) => {
                                    const successful = await updateOrganization({
                                        email: values.email,
                                        orgName: values.orgName,
                                        avatar: values._avatar,
                                        phone: values.phone,
                                        website: values.website,
                                        address: {
                                            city: values.city,
                                            firstLine: values.address1,
                                            secondLine: values.address2,
                                            state: values.state,
                                            zipCode: values.zip,
                                        },
                                    });
                                    try {
                                        const updatedCodeSuccessful = await updateAccountGoogleAnalyticsId(
                                            values.googleTrackingCode
                                        );
                                    } catch (ex) {
                                        console.error(ex)
                                    }
                                    if (successful) {
                                        $('#organization-update').modal('hide');
                                        pullAccountDetails();
                                    } else {
                                        alert(
                                            'There was an error updating the account. Please try again.'
                                        );
                                    }
                                }}
                            />

                            <header>
                                <h5 style={{ display: 'inline', marginRight: 10 }}>Company Info</h5>

                                <div className="t-edit" style={{ display: 'inline' }}>
                                    <a
                                        href="#"
                                        data-toggle="modal"
                                        data-target="#organization-update"
                                        title="Edit">
                                        <i className="fas fa-edit" />
                                    </a>
                                </div>
                            </header>

                            <dl>
                                <dt key="1-o">Organization Name</dt>
                                <dd key="2-o">{accountDetails.organization.orgName}</dd>
                                <dt key="3-o">Organization Address</dt>
                                {expandAddress(accountDetails.organization.address)}
                            </dl>
                        </section>
                    </div>

                    <div className="col">
                        <section className="container" style={sectionStyle}>
                            <dl>
                                <dt key="1-o">Main Phone</dt>
                                <dd key="2-o">{accountDetails.organization.phone}</dd>
                                <dt key="3-o">Main Email</dt>
                                <dd key="4-o">{accountDetails.organization.email}</dd>
                                <dt key="5-o">Website</dt>
                                <dd key="6-o">{accountDetails.organization.website}</dd>
                                <dt key="7-o">Google Analytics Tracking ID</dt>
                                <dd key="8-o">{googleTrackingCode || '--'}</dd>
                            </dl>
                        </section>
                    </div>
                </div>

                {/* <div className="row">
                    <div className="container fluid" style={sectionStyle}>
                        <div className="col-xs-12">
                            <h5 style={sectionHeaderStyle}>Organization Description</h5>
                            <p>Lorem ipsum zift</p>
                        </div>
                    </div>
                </div> */}
            </div>
        </div>
    );
});



// WEBPACK FOOTER //
// ./src/client/profile/index.tsx